import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PowerSupplyOptions,
  StubPlacementOptionNames,
  StubPlacementOptions,
  StubPlacements,
  StubPlacementSet,
} from 'Types/Aggregates';
import { useData } from '../../contexts/data';
import './index.scss';
import FilterSelect from '../FormInputs/FilterSelect';

interface Props {
  inputs: any;
  setInputs: (inputs: any) => void;
}

const StubPlacementInputs = ({ inputs, setInputs }: Props) => {
  const { t } = useTranslation();

  const { activeAggregate, activeUserInputs, setActiveUserInputs } = useData();

  // Update both local and global state
  const updateInput = (name: string, value: any) => {
    const updated = { ...inputs, [name]: value };
    setInputs(updated);
    setActiveUserInputs({ ...activeUserInputs, [name]: value });
  };

  const [
    activeStubPlacement,
    setActiveStubPlacement,
  ] = React.useState<StubPlacements>();
  const [
    stubPlacementOptions,
    setStubPlacementOptions,
  ] = React.useState<StubPlacementSet>();
  const [freshAir, setFreshAir] = React.useState<string>();
  const [extractAir, setExtractAir] = React.useState<string>();
  const [supplyAir, setSupplyhAir] = React.useState<string>();
  const [dischargeAir, setDischargeAir] = React.useState<string>();
  const [version, setVersion] = React.useState<'right' | 'left'>(
    activeUserInputs?.['stub_placement.version'] ||
      activeUserInputs?.stub_placement?.version ||
      'right'
  );

  // Track if we've restored saved placement to avoid overwriting user changes
  const hasRestoredRef = React.useRef(false);

  const rightLeftOptions = [
    { key: 'right', value: 'right', text: t('common.right') },
    { key: 'left', value: 'left', text: t('common.left') },
  ];

  const yesNoOptions = [
    { key: 'true', value: true, text: t('common.yes') },
    { key: 'false', value: false, text: t('common.no') },
  ];

  const getOptions = (field: StubPlacementOptionNames) => {
    const options = Object.keys(StubPlacementOptions)?.filter((option) =>
      stubPlacementOptions?.stub_placements?.find((x) => x[field] === option)
    );
    if (options) {
      return options.map((option) => ({
        key: option,
        value: option,
        text: option,
      }));
    }
    return [];
  };

  const updateFieldVals = (
    id: string | number | undefined,
    placement: StubPlacements | undefined
  ) => {
    const stubPlacementUpdates = {
      'stub_placement.set_id': id,
      'stub_placement.version': version,
      'stub_placement.placement_id': placement?.id,
    };
    setInputs({
      ...inputs,
      ...stubPlacementUpdates,
    });
    setActiveUserInputs({
      ...activeUserInputs,
      ...stubPlacementUpdates,
    });

    if (placement) {
      setActiveStubPlacement(placement);
      setSupplyhAir(placement.supply_air);
      setDischargeAir(placement.discharge_air);
      setExtractAir(placement.extract_air);
      setFreshAir(placement.fresh_air);
    }
  };

  const changeVersion = () => {
    let placements: StubPlacementSet | undefined;
    if (version === 'left') {
      placements = activeAggregate?.stub_placements_left;
    } else {
      placements = activeAggregate?.stub_placements_right;
    }

    // Ensure sorted by ID:
    placements?.stub_placements?.sort((a, b) => (a.id < b.id ? -1 : 1));

    setStubPlacementOptions(placements);

    // Check if we have a saved placement to restore (only on first load)
    const savedPlacementId =
      inputs?.['stub_placement.placement_id'] ||
      activeUserInputs?.['stub_placement.placement_id'];

    if (
      !hasRestoredRef.current &&
      savedPlacementId &&
      placements?.stub_placements?.length
    ) {
      const savedPlacement = placements.stub_placements.find(
        (p) => p.id === savedPlacementId
      );
      if (savedPlacement) {
        hasRestoredRef.current = true;
        updateFieldVals(placements?.id, savedPlacement);
        return;
      }
    }

    // Default: select first placement
    updateFieldVals(
      placements?.id,
      placements?.stub_placements?.length
        ? placements?.stub_placements[0]
        : undefined
    );
  };

  React.useEffect(() => {
    changeVersion();
    // check if combination is valid
  }, [version, activeAggregate]);

  // Sync version from inputs when loading saved data
  React.useEffect(() => {
    const savedVersion =
      inputs?.['stub_placement.version'] ||
      activeUserInputs?.['stub_placement.version'];
    if (savedVersion && savedVersion !== version && !hasRestoredRef.current) {
      setVersion(savedVersion);
    }
  }, [
    inputs?.['stub_placement.version'],
    activeUserInputs?.['stub_placement.version'],
  ]);

  const placementChanged = () => {
    const selected = stubPlacementOptions?.stub_placements.find(
      (o) =>
        o.fresh_air === freshAir &&
        o.extract_air === extractAir &&
        o.supply_air === supplyAir &&
        o.discharge_air === dischargeAir
    );
    if (selected) {
      updateFieldVals(stubPlacementOptions?.id, selected);
    }
  };

  React.useEffect(() => {
    placementChanged();
  }, [freshAir, extractAir, supplyAir, dischargeAir]);

  return (
    <div className="stub-placement-inputs">
      {stubPlacementOptions?.stub_placements?.length && (
        <>
          {activeStubPlacement?.stub_placement_drawing && (
            <img
              src={activeStubPlacement.stub_placement_drawing}
              key={activeStubPlacement.stub_placement_drawing}
              alt="stub placement"
            />
          )}
          <FilterSelect
            name="version"
            label={t('productPage.placement.version')}
            defaultValue={version}
            onChange={(name, value) =>
              setVersion(value === 'right' ? 'right' : 'left')
            }
            options={rightLeftOptions}
          />
          <div className="product-page__inputs__group__wrap">
            <FilterSelect
              name="fresh_air"
              label={t('productPage.placement.airOut') || ''}
              value={freshAir}
              onChange={(name, value) => setFreshAir(value?.toString())}
              options={getOptions('fresh_air')}
            />
            <FilterSelect
              name="extract_air"
              label={t('productPage.placement.airFrom') || ''}
              value={extractAir}
              onChange={(name, value) => setExtractAir(value?.toString())}
              options={getOptions('extract_air')}
            />
          </div>
          <div className="product-page__inputs__group__wrap">
            <FilterSelect
              name="supply_air"
              label={t('productPage.placement.airTo') || ''}
              value={supplyAir}
              onChange={(name, value) => setSupplyhAir(value?.toString())}
              options={getOptions('supply_air')}
            />
            <FilterSelect
              name="discharge_air"
              label={t('productPage.placement.return') || ''}
              value={dischargeAir}
              onChange={(name, value) => setDischargeAir(value?.toString())}
              options={getOptions('discharge_air')}
            />
          </div>
        </>
      )}
      <FilterSelect
        name="stub_placement.outdoorPlacement"
        label={t('productPage.placement.outdoorPlacement')}
        value={
          inputs?.['stub_placement.outdoorPlacement'] ??
          activeUserInputs?.['stub_placement.outdoorPlacement'] ??
          false
        }
        onChange={(name, value) => updateInput(name, value)}
        options={yesNoOptions}
      />
      {activeUserInputs?.power_supply && (
        <FilterSelect
          name="power_supply"
          label={t('productPage.powerSupply')}
          value={
            inputs?.power_supply ??
            activeUserInputs?.power_supply ??
            (activeAggregate?.power_supply === 'BOTH'
              ? 'FOURHUNDRED'
              : activeAggregate?.power_supply)
          }
          selectProps={{
            disabled: activeAggregate?.power_supply !== 'BOTH',
            options: PowerSupplyOptions,
          }}
          onChange={(name, value) => updateInput(name, value)}
          options={PowerSupplyOptions}
        />
      )}
    </div>
  );
};

export default StubPlacementInputs;
